import type { Option } from '@automata/workflow-builder';
import Stack from '@mui/material/Stack';
import { GridFilterPanel } from '@mui/x-data-grid-pro';
import { ReactElement } from 'react';

import { FilterProvider } from './shared/FilterContext';
import { FilterWithDependencies } from '../../filtersManifest';
import { ValidColumnIds } from '../../types';

export enum FilterIds {
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  NAME = 'name',
  TAGS = 'tags',
}

export enum FilterOperators {
  CONTAINS = 'contains',
  ON_OR_AFTER = 'onOrAfter',
  ON_OR_BEFORE = 'onOrBefore',
}

interface Props {
  availableFilters: FilterWithDependencies[];
  typeOptions?: string[];
  tagOptions?: Option[];
  activeField?: string | null;
  panelTrigger?: 'toolbar' | 'column' | null;
}

// required for slots/slotProps in ModelList config after datagrid v7.x
declare module '@mui/x-data-grid-pro' {
  interface FilterPanelPropsOverrides extends Props {}
}

const FilterPanel = ({
  availableFilters,
  typeOptions,
  tagOptions,
  activeField,
  panelTrigger,
  ...restProps
}: Props): ReactElement => {
  // return default component if no filters are available
  if (availableFilters.length === 0) return <GridFilterPanel {...restProps} />;

  const fieldToKey: Record<string, string> = {
    [ValidColumnIds.NAME]: 'nameContains',
    [ValidColumnIds.WORKFLOWS_TYPE]: 'typeMultiSelect',
    [ValidColumnIds.TAGS]: 'tagsMultiSelect',
  };

  const dateKeys = [
    'dateRangePickerModified',
    'dateRangePickerCreated',
    'dateRangePickerAccessed',
    'dateRangePickerStartTime',
  ];

  let keysToRender: string[] = [];

  if (panelTrigger === 'toolbar') {
    keysToRender = dateKeys;
  } else if (panelTrigger === 'column') {
    if (activeField && fieldToKey[activeField]) {
      keysToRender = [...Object.values(fieldToKey), ...dateKeys];
    } else if (
      activeField === ValidColumnIds.DATE_MODIFIED ||
      activeField === ValidColumnIds.DATE_CREATED ||
      activeField === ValidColumnIds.DATE_ACCESSED ||
      activeField === ValidColumnIds.RUN_START_TIME
    ) {
      keysToRender = dateKeys;
    } else {
      keysToRender = [...Object.values(fieldToKey), ...dateKeys];
    }
  } else {
    keysToRender = [...Object.values(fieldToKey), ...dateKeys];
  }

  const filtersToRender = availableFilters.filter((f) =>
    keysToRender.includes(f.key)
  );

  if (filtersToRender.length === 0) return <GridFilterPanel {...restProps} />;

  return (
    <FilterProvider
      value={{ typeOptions: typeOptions ?? [], tagOptions: tagOptions ?? [] }}
    >
      <Stack>
        {filtersToRender.map(({ component: Filter, key }) => (
          <Filter key={key} />
        ))}
      </Stack>
    </FilterProvider>
  );
};

export default FilterPanel;
